import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useId, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Loader2, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  getMenuEditor, updateMenu, upsertMenuTranslation, publishMenu, unpublishMenu,
  createCategory, updateCategory, deleteCategory, upsertCategoryTranslation,
  createItem, updateItem, deleteItem, upsertItemTranslation, setItemAllergens,
  createMenuImageSignedUpload, getMenuImageSignedUrl,
} from "@/lib/menu.functions";
import { PublishMenuDialog } from "@/components/menu/PublishMenuDialog";
import { LiveAnnouncerProvider, useAnnounce } from "@/components/a11y/LiveAnnouncer";
import { useMenuOwnerT } from "@/lib/menu-owner-i18n";

export const Route = createFileRoute("/moje-podniky/$id/menu/$menuId")({
  ssr: false,
  component: MenuEditorPage,
  head: () => ({ meta: [
    { title: "Editor menu — Cerveza.International" },
    { name: "robots", content: "noindex,nofollow" },
  ] }),
});

function MenuEditorPage() {
  const { id: businessId, menuId } = Route.useParams();
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const t = useMenuOwnerT();
  const getFn = useServerFn(getMenuEditor);

  const { data, isLoading } = useQuery({
    queryKey: ["menu-editor", menuId],
    queryFn: () => getFn({ data: { menuId } }),
    enabled: !!user,
  });

  const { data: allergens = [] } = useQuery({
    queryKey: ["allergens"],
    queryFn: async () => {
      const { data } = await supabase.from("allergens" as any).select("id, code, sort_order").order("sort_order");
      return ((data as unknown) ?? []) as { id: number; code: string; sort_order: number }[];
    },
  });

  const menuLang = (data?.menu as any)?.default_language as string | undefined;
  const { data: allergenTr = [] } = useQuery({
    queryKey: ["allergen-tr", menuLang],
    enabled: !!menuLang,
    queryFn: async () => {
      const { data } = await supabase
        .from("allergen_translations" as any)
        .select("allergen_id, name")
        .eq("language_code", menuLang!);
      return ((data as unknown) ?? []) as { allergen_id: number; name: string }[];
    },
  });
  const allergenLabels = useMemo(() => {
    const m: Record<number, string> = {};
    for (const a of allergens) m[a.id] = a.code;
    for (const tr of allergenTr) m[tr.allergen_id] = tr.name;
    return m;
  }, [allergens, allergenTr]);


  if (loading || isLoading || !data) {
    return <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">{t("common.loading")}</div>;
  }
  if (!user) return <div className="grid min-h-screen place-items-center"><Link to="/auth" className="text-primary underline">{t("nav.signIn")}</Link></div>;

  const menu = data.menu as any;

  return (
    <LiveAnnouncerProvider>
    <div className="min-h-screen bg-muted/30 px-3 py-6 sm:px-4 sm:py-10">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/moje-podniky/$id/menu" params={{ id: businessId }}>
                <ArrowLeft className="mr-1 h-4 w-4" /> {t("nav.back")}
              </Link>
            </Button>
            <h1 className="mt-2 font-serif text-2xl sm:text-3xl">{menu.internal_name}</h1>
            <div className="mt-1 flex flex-wrap gap-2 text-xs">
              <Badge>{t(`status.${menu.status}`)}</Badge>
              <Badge variant="outline">{t(`type.${menu.menu_type}`)}</Badge>
              <Badge variant="outline">{menu.default_language.toUpperCase()}</Badge>
              <Badge variant="outline">{menu.currency_code}</Badge>
              <Badge variant="outline">v{menu.version}</Badge>
            </div>
          </div>
          <PublishActions menu={menu} onDone={() => qc.invalidateQueries({ queryKey: ["menu-editor", menuId] })} />
        </div>

        <Tabs defaultValue="basic">
          <TabsList className="flex-wrap">
            <TabsTrigger value="basic">{t("ed.tab.basic")}</TabsTrigger>
            <TabsTrigger value="content">{t("ed.tab.content")}</TabsTrigger>
            <TabsTrigger value="translations">{t("ed.tab.translations")}</TabsTrigger>
            <TabsTrigger value="preview">{t("ed.tab.preview")}</TabsTrigger>
          </TabsList>

          <TabsContent value="basic"><BasicTab menu={menu} translations={data.translations as any[]} /></TabsContent>
          <TabsContent value="content"><ContentTab data={data} allergens={allergens} allergenLabels={allergenLabels} /></TabsContent>
          <TabsContent value="translations"><TranslationsTab data={data} /></TabsContent>
          <TabsContent value="preview"><PreviewTab data={data} allergens={allergens} /></TabsContent>

        </Tabs>
      </div>
    </div>
    </LiveAnnouncerProvider>
  );
}

function PublishActions({ menu, onDone }: { menu: any; onDone: () => void }) {
  const t = useMenuOwnerT();
  const unpubFn = useServerFn(unpublishMenu);
  const announce = useAnnounce();
  const [dialogOpen, setDialogOpen] = useState(false);
  const unpub = useMutation({
    mutationFn: () => unpubFn({ data: { menuId: menu.id } }),
    onMutate: () => announce(t("pub.unpublishing"), "polite"),
    onSuccess: () => {
      announce(t("pub.unpublished"), "assertive");
      toast.success(t("pub.unpublishToast"));
      onDone();
    },
    onError: (e: Error) => {
      announce(t("pub.unpublishFailed", { msg: e.message }), "assertive");
      toast.error(e.message);
    },
  });
  if (menu.status === "published") {
    return <Button variant="outline" onClick={() => unpub.mutate()}>{t("pub.unpublish")}</Button>;
  }
  return (
    <>
      <Button onClick={() => setDialogOpen(true)}>
        <Send className="mr-2 h-4 w-4" />
        {t("pub.publish")}
      </Button>
      <PublishMenuDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        menu={menu}
        onPublished={onDone}
      />
    </>
  );
}

function BasicTab({ menu, translations }: { menu: any; translations: any[] }) {
  const t = useMenuOwnerT();
  const qc = useQueryClient();
  const announce = useAnnounce();
  const upFn = useServerFn(updateMenu);
  const trFn = useServerFn(upsertMenuTranslation);
  const [form, setForm] = useState({
    internal_name: menu.internal_name ?? "",
    public_name: menu.public_name ?? "",
    default_language: menu.default_language,
    currency_code: menu.currency_code,
    valid_from: menu.valid_from ? menu.valid_from.slice(0, 16) : "",
    valid_to: menu.valid_to ? menu.valid_to.slice(0, 16) : "",
    include_images_in_pdf: !!menu.include_images_in_pdf,
    available_languages: (menu.available_languages ?? []).join(","),
  });
  const defTr = translations.find((tr) => tr.language_code === menu.default_language) ?? { title: "", description: "", footer_note: "" };
  const [tr, setTr] = useState({
    title: defTr.title ?? "",
    description: defTr.description ?? "",
    footer_note: defTr.footer_note ?? "",
  });

  const save = useMutation({
    mutationFn: async () => {
      await upFn({ data: { menuId: menu.id, patch: {
        internal_name: form.internal_name,
        public_name: form.public_name || null,
        default_language: form.default_language,
        currency_code: form.currency_code,
        valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
        valid_to: form.valid_to ? new Date(form.valid_to).toISOString() : null,
        include_images_in_pdf: form.include_images_in_pdf,
        available_languages: form.available_languages.split(",").map((s: string) => s.trim()).filter(Boolean),
      } } });
      await trFn({ data: { menuId: menu.id, language_code: form.default_language, ...tr } });
    },
    onSuccess: () => { toast.success(t("common.saved")); announce(t("basic.savedAnnounce"), "polite"); qc.invalidateQueries({ queryKey: ["menu-editor", menu.id] }); },
    onError: (e: Error) => { toast.error(e.message); announce(t("common.saveFailed", { msg: e.message }), "assertive"); },
  });

  return (
    <Card>
      <CardContent className="grid gap-4 py-6 md:grid-cols-2">
        <div><Label>{t("basic.internalName")}</Label><Input value={form.internal_name} onChange={(e) => setForm({ ...form, internal_name: e.target.value })} /></div>
        <div><Label>{t("basic.publicName")}</Label><Input value={form.public_name} onChange={(e) => setForm({ ...form, public_name: e.target.value })} /></div>
        <div><Label>{t("basic.mainLang")}</Label><Input value={form.default_language} onChange={(e) => setForm({ ...form, default_language: e.target.value.toLowerCase() })} /></div>
        <div><Label>{t("basic.currency")}</Label><Input value={form.currency_code} maxLength={3} onChange={(e) => setForm({ ...form, currency_code: e.target.value.toUpperCase() })} /></div>
        <div><Label>{t("basic.validFrom")}</Label><Input type="datetime-local" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} /></div>
        <div><Label>{t("basic.validTo")}</Label><Input type="datetime-local" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} /></div>
        <div className="md:col-span-2"><Label>{t("basic.availableLangs")}</Label>
          <Input value={form.available_languages} onChange={(e) => setForm({ ...form, available_languages: e.target.value })} /></div>
        <div className="flex items-center gap-2 md:col-span-2">
          <Switch checked={form.include_images_in_pdf} onCheckedChange={(v) => setForm({ ...form, include_images_in_pdf: v })} />
          <Label>{t("basic.imagesInPdf")}</Label>
        </div>

        <div className="md:col-span-2 mt-4 rounded border bg-muted/40 p-4">
          <p className="mb-2 text-sm font-medium">{t("basic.textOf", { lang: form.default_language })}</p>
          <div className="grid gap-3">
            <div><Label>{t("basic.title")}</Label><Input value={tr.title} onChange={(e) => setTr({ ...tr, title: e.target.value })} /></div>
            <div><Label>{t("basic.description")}</Label><Textarea value={tr.description} onChange={(e) => setTr({ ...tr, description: e.target.value })} rows={3} /></div>
            <div><Label>{t("basic.footer")}</Label><Textarea value={tr.footer_note} onChange={(e) => setTr({ ...tr, footer_note: e.target.value })} rows={2} /></div>
          </div>
        </div>

        <div className="md:col-span-2">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} {t("common.save")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ContentTab({ data, allergens, allergenLabels }: { data: any; allergens: { id: number; code: string }[]; allergenLabels: Record<number, string> }) {
  const t = useMenuOwnerT();
  const qc = useQueryClient();
  const menu = data.menu;
  const catCreateFn = useServerFn(createCategory);
  const catUpdateFn = useServerFn(updateCategory);
  const catDelFn = useServerFn(deleteCategory);
  const catTrFn = useServerFn(upsertCategoryTranslation);
  const itemCreateFn = useServerFn(createItem);
  const itemUpdateFn = useServerFn(updateItem);
  const itemDelFn = useServerFn(deleteItem);
  const itemTrFn = useServerFn(upsertItemTranslation);
  const setAllFn = useServerFn(setItemAllergens);
  const uploadFn = useServerFn(createMenuImageSignedUpload);

  const [newCat, setNewCat] = useState("");
  const [newCatError, setNewCatError] = useState<string | null>(null);
  const [catRenameError, setCatRenameError] = useState<Record<string, string | null>>({});
  const announce = useAnnounce();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["menu-editor", menu.id] });

  const catName = (catId: string) => {
    const tr = data.categoryTranslations.find((x: any) => x.category_id === catId && x.language_code === menu.default_language)
      ?? data.categoryTranslations.find((x: any) => x.category_id === catId);
    return tr?.name ?? t("common.noName");
  };
  const itemName = (itemId: string) => {
    const tr = data.itemTranslations.find((x: any) => x.item_id === itemId && x.language_code === menu.default_language)
      ?? data.itemTranslations.find((x: any) => x.item_id === itemId);
    return tr?.name ?? "";
  };

  const langUp = menu.default_language.toUpperCase();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("cat.new")}</CardTitle>
          <p id="new-category-help" className="text-sm text-muted-foreground">
            {t("cat.newHelp", { lang: langUp })}
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
            <div className="flex-1 space-y-1">
              <Label htmlFor="new-category-name">{t("cat.name")}</Label>
              <Input
                id="new-category-name"
                placeholder={t("cat.namePh")}
                value={newCat}
                aria-describedby="new-category-help new-category-error"
                aria-invalid={!!newCatError}
                aria-required="true"
                onChange={(e) => { setNewCat(e.target.value); if (newCatError) setNewCatError(null); }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    (document.getElementById("new-category-submit") as HTMLButtonElement | null)?.click();
                  }
                }}
              />
              <p id="new-category-error" role="alert" aria-live="polite" className={`text-sm ${newCatError ? "text-destructive" : "sr-only"}`}>
                {newCatError ?? ""}
              </p>
            </div>
            <Button
              id="new-category-submit"
              type="button"
              onClick={async () => {
                const name = newCat.trim();
                if (!name) {
                  setNewCatError(t("cat.emptyName"));
                  announce(t("cat.nameReqAnnounce"), "assertive");
                  return;
                }
                const exists = data.categories.some((c: any) => catName(c.id).toLowerCase() === name.toLowerCase());
                if (exists) {
                  setNewCatError(t("cat.exists", { name }));
                  announce(t("cat.existsAnnounce"), "assertive");
                  return;
                }
                try {
                  await catCreateFn({ data: { menuId: menu.id, name, language_code: menu.default_language } });
                  setNewCat("");
                  setNewCatError(null);
                  announce(t("cat.createdAnnounce", { name }), "polite");
                  invalidate();
                } catch (err: any) {
                  const msg = err?.message ?? t("cat.saveFailed");
                  setNewCatError(msg);
                  announce(msg, "assertive");
                }
              }}
            >
              <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
              {t("cat.add")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data.categories.map((cat: any) => {
        const items = data.items.filter((i: any) => i.category_id === cat.id);
        const renameErr = catRenameError[cat.id] ?? null;
        const renameErrorId = `cat-${cat.id}-rename-error`;
        return (
          <Card key={cat.id}>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <div className="flex flex-1 flex-col gap-1">
                <Label htmlFor={`cat-${cat.id}-name`} className="text-xs text-muted-foreground">
                  {t("cat.renameLabel", { lang: langUp })}
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id={`cat-${cat.id}-name`}
                    defaultValue={catName(cat.id)}
                    aria-describedby={renameErr ? renameErrorId : undefined}
                    aria-invalid={!!renameErr}
                    onBlur={async (e) => {
                      const v = e.target.value.trim();
                      if (!v) {
                        setCatRenameError((s) => ({ ...s, [cat.id]: t("cat.nameEmpty") }));
                        announce(t("cat.nameEmpty"), "assertive");
                        e.target.value = catName(cat.id);
                        return;
                      }
                      if (v === catName(cat.id)) return;
                      try {
                        await catTrFn({ data: { categoryId: cat.id, language_code: menu.default_language, name: v } });
                        setCatRenameError((s) => ({ ...s, [cat.id]: null }));
                        announce(t("cat.renamed", { name: v }), "polite");
                        invalidate();
                      } catch (err: any) {
                        const msg = err?.message ?? t("cat.renameFailed");
                        setCatRenameError((s) => ({ ...s, [cat.id]: msg }));
                        announce(msg, "assertive");
                      }
                    }}
                    className="max-w-sm"
                  />
                  <div className="flex items-center gap-1">
                    <Switch checked={cat.is_active} onCheckedChange={async (v) => { await catUpdateFn({ data: { categoryId: cat.id, is_active: v } }); invalidate(); }} />
                    <span className="text-xs text-muted-foreground">{t("cat.active")}</span>
                  </div>
                </div>
                {renameErr && (
                  <p id={renameErrorId} role="alert" aria-live="polite" className="text-sm text-destructive">
                    {renameErr}
                  </p>
                )}
              </div>
              <Button size="sm" variant="ghost" aria-label={t("cat.deleteAria", { name: catName(cat.id) })} onClick={async () => { if (confirm(t("cat.deleteConfirm"))) { await catDelFn({ data: { categoryId: cat.id } }); invalidate(); } }}>
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </Button>
            </CardHeader>
            <CardContent className="divide-y divide-border/60 [&>*]:py-4 first:[&>*]:pt-0 last:[&>*]:pb-0">
              {items.map((it: any) => (
                <ItemRow
                  key={it.id} item={it} menu={menu} allergens={allergens} allergenLabels={allergenLabels}
                  data={data} onChange={invalidate}
                  itemUpdateFn={itemUpdateFn} itemDelFn={itemDelFn} itemTrFn={itemTrFn} setAllFn={setAllFn} uploadFn={uploadFn}
                  initialName={itemName(it.id)}
                />

              ))}
              <NewItemRow categoryId={cat.id} defaultLang={menu.default_language} onCreate={async (p) => {
                await itemCreateFn({ data: { categoryId: cat.id, language_code: menu.default_language, name: p.name, price: p.price, portion_size: p.portion_size } });
                invalidate();
              }} />
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function NewItemRow({ categoryId: _categoryId, defaultLang: _defaultLang, onCreate }: { categoryId: string; defaultLang: string; onCreate: (p: { name: string; price?: number; portion_size?: string }) => Promise<void> }) {
  const t = useMenuOwnerT();
  const [name, setName] = useState(""); const [price, setPrice] = useState(""); const [portion, setPortion] = useState("");
  const [errors, setErrors] = useState<{ name?: string; price?: string }>({});
  const announce = useAnnounce();
  const submit = async () => {
    const next: { name?: string; price?: string } = {};
    if (!name.trim()) next.name = t("item.nameReq");
    let p: number | undefined;
    if (price.trim()) {
      const parsed = Number(price.replace(",", "."));
      if (!Number.isFinite(parsed) || parsed < 0) next.price = t("item.priceInvalid");
      else p = parsed;
    }
    setErrors(next);
    if (Object.keys(next).length) {
      announce(t("item.cantAdd", { errs: Object.values(next).join(" ") }), "assertive");
      return;
    }
    await onCreate({ name: name.trim(), price: p, portion_size: portion || undefined });
    const added = name.trim();
    setName(""); setPrice(""); setPortion(""); setErrors({});
    announce(t("item.addedAnnounce", { name: added }), "polite");
  };
  const uid = useId();
  const nameId = `${uid}-name`; const priceId = `${uid}-price`; const portionId = `${uid}-portion`;
  const onKey = (e: React.KeyboardEvent) => { if (e.key === "Enter") { e.preventDefault(); submit(); } };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed p-3 sm:grid sm:grid-cols-[1fr_110px_130px_auto] sm:items-start sm:gap-2" onKeyDown={onKey}>
      <div className="grid gap-1">
        <Label htmlFor={nameId} className="text-xs text-muted-foreground sm:sr-only">{t("item.namePh")}</Label>
        <Input id={nameId} placeholder={t("item.namePh")} value={name}
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? `${nameId}-err` : undefined}
          className={errors.name ? "border-destructive focus-visible:ring-destructive" : ""}
          onChange={(e) => { setName(e.target.value); if (errors.name && e.target.value.trim()) setErrors((s) => ({ ...s, name: undefined })); }} />
        {errors.name && <p id={`${nameId}-err`} role="alert" className="text-xs text-destructive">{errors.name}</p>}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:contents">
        <div className="grid gap-1">
          <Label htmlFor={priceId} className="text-xs text-muted-foreground sm:sr-only">{t("item.price")}</Label>
          <Input id={priceId} placeholder={t("item.pricePh")} inputMode="decimal" value={price}
            aria-invalid={!!errors.price}
            aria-describedby={errors.price ? `${priceId}-err` : undefined}
            className={errors.price ? "border-destructive focus-visible:ring-destructive" : ""}
            onChange={(e) => { setPrice(e.target.value); if (errors.price) setErrors((s) => ({ ...s, price: undefined })); }} />
          {errors.price && <p id={`${priceId}-err`} role="alert" className="text-xs text-destructive">{errors.price}</p>}
        </div>
        <div className="grid gap-1">
          <Label htmlFor={portionId} className="text-xs text-muted-foreground sm:sr-only">{t("item.portion")}</Label>
          <Input id={portionId} placeholder={t("item.portionPh")} value={portion} onChange={(e) => setPortion(e.target.value)} />
        </div>
      </div>
      <Button size="sm" className="w-full sm:w-auto" aria-label={t("item.addAria")} onClick={submit}><Plus className="h-4 w-4" aria-hidden="true" /></Button>
    </div>
  );
}


const MENU_LABELS: Record<string, {
  vegetarian: string; vegan: string; gluten_free: string; lactose_free: string;
  diet: string; contains: string; may_contain: string; save_allergens: string;
  photo: string; upload_hint: string; no_photo: string; remove_photo: string;
  description: string; expand: string; collapse: string; delete: string;
  available: string; sold_out: string; days_only: string; time_only: string; hidden: string;
}> = {
  cs: { vegetarian:"vegetariánské", vegan:"veganské", gluten_free:"bez lepku", lactose_free:"bez laktózy", diet:"Dietní", contains:"Obsahuje alergeny", may_contain:"Může obsahovat", save_allergens:"Uložit alergeny", photo:"Fotografie položky", upload_hint:"JPEG nebo PNG, max 5 MB.", no_photo:"bez fotografie", remove_photo:"Odstranit fotografii", description:"Popis", expand:"Detail", collapse:"Sbalit", delete:"Smazat?", available:"Dostupné", sold_out:"Vyprodáno", days_only:"Určité dny", time_only:"Určitý čas", hidden:"Skryté" },
  sk: { vegetarian:"vegetariánske", vegan:"vegánske", gluten_free:"bez lepku", lactose_free:"bez laktózy", diet:"Diétne", contains:"Obsahuje alergény", may_contain:"Môže obsahovať", save_allergens:"Uložiť alergény", photo:"Fotografia položky", upload_hint:"JPEG alebo PNG, max 5 MB.", no_photo:"bez fotografie", remove_photo:"Odstrániť fotografiu", description:"Popis", expand:"Detail", collapse:"Zbaliť", delete:"Zmazať?", available:"Dostupné", sold_out:"Vypredané", days_only:"Určité dni", time_only:"Určitý čas", hidden:"Skryté" },
  pl: { vegetarian:"wegetariańskie", vegan:"wegańskie", gluten_free:"bez glutenu", lactose_free:"bez laktozy", diet:"Dieta", contains:"Zawiera alergeny", may_contain:"Może zawierać", save_allergens:"Zapisz alergeny", photo:"Zdjęcie pozycji", upload_hint:"JPEG lub PNG, maks. 5 MB.", no_photo:"brak zdjęcia", remove_photo:"Usuń zdjęcie", description:"Opis", expand:"Szczegóły", collapse:"Zwiń", delete:"Usunąć?", available:"Dostępne", sold_out:"Wyprzedane", days_only:"Wybrane dni", time_only:"Wybrany czas", hidden:"Ukryte" },
  en: { vegetarian:"vegetarian", vegan:"vegan", gluten_free:"gluten free", lactose_free:"lactose free", diet:"Diet", contains:"Contains allergens", may_contain:"May contain", save_allergens:"Save allergens", photo:"Item photo", upload_hint:"JPEG or PNG, max 5 MB.", no_photo:"no photo", remove_photo:"Remove photo", description:"Description", expand:"Details", collapse:"Collapse", delete:"Delete?", available:"Available", sold_out:"Sold out", days_only:"Selected days", time_only:"Selected time", hidden:"Hidden" },
  de: { vegetarian:"vegetarisch", vegan:"vegan", gluten_free:"glutenfrei", lactose_free:"laktosefrei", diet:"Ernährung", contains:"Enthält Allergene", may_contain:"Kann enthalten", save_allergens:"Allergene speichern", photo:"Foto des Gerichts", upload_hint:"JPEG oder PNG, max. 5 MB.", no_photo:"kein Foto", remove_photo:"Foto entfernen", description:"Beschreibung", expand:"Details", collapse:"Einklappen", delete:"Löschen?", available:"Verfügbar", sold_out:"Ausverkauft", days_only:"Bestimmte Tage", time_only:"Bestimmte Zeit", hidden:"Verborgen" },
  es: { vegetarian:"vegetariano", vegan:"vegano", gluten_free:"sin gluten", lactose_free:"sin lactosa", diet:"Dieta", contains:"Contiene alérgenos", may_contain:"Puede contener", save_allergens:"Guardar alérgenos", photo:"Foto del plato", upload_hint:"JPEG o PNG, máx. 5 MB.", no_photo:"sin foto", remove_photo:"Eliminar foto", description:"Descripción", expand:"Detalles", collapse:"Contraer", delete:"¿Eliminar?", available:"Disponible", sold_out:"Agotado", days_only:"Días concretos", time_only:"Horario concreto", hidden:"Oculto" },
  pt: { vegetarian:"vegetariano", vegan:"vegano", gluten_free:"sem glúten", lactose_free:"sem lactose", diet:"Dieta", contains:"Contém alérgenos", may_contain:"Pode conter", save_allergens:"Guardar alérgenos", photo:"Foto do prato", upload_hint:"JPEG ou PNG, máx. 5 MB.", no_photo:"sem foto", remove_photo:"Remover foto", description:"Descrição", expand:"Detalhes", collapse:"Recolher", delete:"Eliminar?", available:"Disponível", sold_out:"Esgotado", days_only:"Dias específicos", time_only:"Horário específico", hidden:"Oculto" },
  fr: { vegetarian:"végétarien", vegan:"végan", gluten_free:"sans gluten", lactose_free:"sans lactose", diet:"Régime", contains:"Contient des allergènes", may_contain:"Peut contenir", save_allergens:"Enregistrer les allergènes", photo:"Photo du plat", upload_hint:"JPEG ou PNG, max 5 Mo.", no_photo:"sans photo", remove_photo:"Supprimer la photo", description:"Description", expand:"Détails", collapse:"Réduire", delete:"Supprimer ?", available:"Disponible", sold_out:"Épuisé", days_only:"Jours définis", time_only:"Horaire défini", hidden:"Masqué" },
  it: { vegetarian:"vegetariano", vegan:"vegano", gluten_free:"senza glutine", lactose_free:"senza lattosio", diet:"Dieta", contains:"Contiene allergeni", may_contain:"Può contenere", save_allergens:"Salva allergeni", photo:"Foto del piatto", upload_hint:"JPEG o PNG, max 5 MB.", no_photo:"nessuna foto", remove_photo:"Rimuovi foto", description:"Descrizione", expand:"Dettagli", collapse:"Comprimi", delete:"Eliminare?", available:"Disponibile", sold_out:"Esaurito", days_only:"Giorni specifici", time_only:"Orario specifico", hidden:"Nascosto" },
  hr: { vegetarian:"vegetarijansko", vegan:"vegansko", gluten_free:"bez glutena", lactose_free:"bez laktoze", diet:"Prehrana", contains:"Sadrži alergene", may_contain:"Može sadržavati", save_allergens:"Spremi alergene", photo:"Fotografija jela", upload_hint:"JPEG ili PNG, maks. 5 MB.", no_photo:"bez fotografije", remove_photo:"Ukloni fotografiju", description:"Opis", expand:"Detalji", collapse:"Sažmi", delete:"Obrisati?", available:"Dostupno", sold_out:"Rasprodano", days_only:"Određeni dani", time_only:"Određeno vrijeme", hidden:"Skriveno" },
  ro: { vegetarian:"vegetarian", vegan:"vegan", gluten_free:"fără gluten", lactose_free:"fără lactoză", diet:"Dietă", contains:"Conține alergeni", may_contain:"Poate conține", save_allergens:"Salvează alergenii", photo:"Fotografie preparat", upload_hint:"JPEG sau PNG, max 5 MB.", no_photo:"fără fotografie", remove_photo:"Șterge fotografia", description:"Descriere", expand:"Detalii", collapse:"Restrânge", delete:"Ștergeți?", available:"Disponibil", sold_out:"Epuizat", days_only:"Zile specifice", time_only:"Interval orar", hidden:"Ascuns" },
  hu: { vegetarian:"vegetáriánus", vegan:"vegán", gluten_free:"gluténmentes", lactose_free:"laktózmentes", diet:"Diéta", contains:"Allergéneket tartalmaz", may_contain:"Tartalmazhat", save_allergens:"Allergének mentése", photo:"Étel fotója", upload_hint:"JPEG vagy PNG, max. 5 MB.", no_photo:"nincs fotó", remove_photo:"Fotó törlése", description:"Leírás", expand:"Részletek", collapse:"Összecsuk", delete:"Törli?", available:"Elérhető", sold_out:"Elfogyott", days_only:"Bizonyos napok", time_only:"Bizonyos idő", hidden:"Rejtett" },
  bg: { vegetarian:"вегетарианско", vegan:"веган", gluten_free:"без глутен", lactose_free:"без лактоза", diet:"Диета", contains:"Съдържа алергени", may_contain:"Може да съдържа", save_allergens:"Запази алергените", photo:"Снимка на ястието", upload_hint:"JPEG или PNG, макс. 5 MB.", no_photo:"без снимка", remove_photo:"Премахни снимката", description:"Описание", expand:"Детайли", collapse:"Свий", delete:"Изтриване?", available:"Налично", sold_out:"Изчерпано", days_only:"Определени дни", time_only:"Определено време", hidden:"Скрито" },
};
const ml = (lang: string) => MENU_LABELS[lang] ?? MENU_LABELS.en;

/** Localized labels for the photo uploader status (loading / add / replace). */
const UPLOAD_LABELS: Record<string, { add: string; replace: string; uploading: string; status_uploading: string; status_ready: string; status_error: string }> = {
  cs: { add: "Přidat foto", replace: "Vyměnit foto", uploading: "Nahrávám…", status_uploading: "Nahrávám fotografii, čekejte prosím.", status_ready: "Fotografie je nahraná.", status_error: "Nahrávání fotografie selhalo." },
  sk: { add: "Pridať foto", replace: "Vymeniť foto", uploading: "Nahrávam…", status_uploading: "Nahrávam fotografiu, čakajte prosím.", status_ready: "Fotografia je nahraná.", status_error: "Nahrávanie fotografie zlyhalo." },
  pl: { add: "Dodaj zdjęcie", replace: "Zmień zdjęcie", uploading: "Przesyłanie…", status_uploading: "Trwa przesyłanie zdjęcia, proszę czekać.", status_ready: "Zdjęcie zostało przesłane.", status_error: "Przesyłanie zdjęcia nie powiodło się." },
  en: { add: "Add photo", replace: "Replace photo", uploading: "Uploading…", status_uploading: "Uploading photo, please wait.", status_ready: "Photo uploaded.", status_error: "Photo upload failed." },
  de: { add: "Foto hinzufügen", replace: "Foto ersetzen", uploading: "Wird hochgeladen…", status_uploading: "Foto wird hochgeladen, bitte warten.", status_ready: "Foto hochgeladen.", status_error: "Foto-Upload fehlgeschlagen." },
  es: { add: "Añadir foto", replace: "Cambiar foto", uploading: "Subiendo…", status_uploading: "Subiendo foto, espere por favor.", status_ready: "Foto subida.", status_error: "Error al subir la foto." },
  pt: { add: "Adicionar foto", replace: "Substituir foto", uploading: "A enviar…", status_uploading: "A enviar foto, aguarde.", status_ready: "Foto enviada.", status_error: "Falha ao enviar a foto." },
  fr: { add: "Ajouter une photo", replace: "Remplacer la photo", uploading: "Téléversement…", status_uploading: "Téléversement de la photo, veuillez patienter.", status_ready: "Photo téléversée.", status_error: "Échec du téléversement." },
  it: { add: "Aggiungi foto", replace: "Sostituisci foto", uploading: "Caricamento…", status_uploading: "Caricamento foto, attendere.", status_ready: "Foto caricata.", status_error: "Caricamento non riuscito." },
  hr: { add: "Dodaj fotku", replace: "Zamijeni fotku", uploading: "Učitavanje…", status_uploading: "Učitavanje fotografije, pričekajte.", status_ready: "Fotografija je učitana.", status_error: "Učitavanje nije uspjelo." },
  ro: { add: "Adaugă foto", replace: "Înlocuiește foto", uploading: "Se încarcă…", status_uploading: "Se încarcă fotografia, așteptați.", status_ready: "Fotografie încărcată.", status_error: "Încărcarea a eșuat." },
  hu: { add: "Fotó hozzáadása", replace: "Fotó cseréje", uploading: "Feltöltés…", status_uploading: "Fotó feltöltése, kérem várjon.", status_ready: "Fotó feltöltve.", status_error: "A fotó feltöltése sikertelen." },
  bg: { add: "Добави снимка", replace: "Смени снимката", uploading: "Качване…", status_uploading: "Качване на снимката, моля изчакайте.", status_ready: "Снимката е качена.", status_error: "Качването не бе успешно." },
};
const ul = (lang: string) => UPLOAD_LABELS[lang] ?? UPLOAD_LABELS.en;


function ItemRow({ item, menu, allergens, allergenLabels, data, onChange, itemUpdateFn, itemDelFn, itemTrFn, setAllFn, uploadFn, initialName }: any) {
  const t = useMenuOwnerT();
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState(initialName);
  const trDesc = data.itemTranslations.find((x: any) => x.item_id === item.id && x.language_code === menu.default_language)?.description ?? "";
  const [desc, setDesc] = useState(trDesc);
  const contains = data.itemAllergens.filter((a: any) => a.item_id === item.id && a.presence_type === "contains").map((a: any) => a.allergen_id);
  const may = data.itemAllergens.filter((a: any) => a.item_id === item.id && a.presence_type === "may_contain").map((a: any) => a.allergen_id);
  const [cSet, setCSet] = useState<number[]>(contains);
  const [mSet, setMSet] = useState<number[]>(may);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; price?: string; photo?: string }>({});
  const setErr = (k: keyof typeof errors, v?: string) => setErrors((s) => ({ ...s, [k]: v }));
  const announce = useAnnounce();
  const getUrlFn = useServerFn(getMenuImageSignedUrl);

  const { data: thumbUrl } = useQuery({
    queryKey: ["menu-item-full-image", item.id, item.image_path],
    queryFn: () => item.image_path ? getUrlFn({ data: { path: item.image_path } }) : Promise.resolve(null),
    enabled: !!item.image_path,
    staleTime: 30 * 60 * 1000,
  });

  const toggle = (arr: number[], id: number) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  const onFile = async (file: File) => {
    setErr("photo", undefined);
    const okType = file.type === "image/jpeg" || file.type === "image/png";
    if (!okType) { setErr("photo", t("photo.jpegOnly")); announce(t("photo.jpegOnlyAnnounce"), "assertive"); return; }
    if (file.size > 5 * 1024 * 1024) { setErr("photo", t("photo.tooLarge")); announce(t("photo.tooLargeAnnounce"), "assertive"); return; }
    setUploading(true);
    announce(t("photo.uploadingAnnounce"), "polite");
    try {
      const ext = file.type === "image/png" ? "png" : "jpg";
      const s = await uploadFn({ data: { itemId: item.id, ext } });
      const up = await fetch(s.signedUrl, { method: "PUT", headers: { "Content-Type": file.type }, body: file });
      if (!up.ok) throw new Error(t("photo.uploadFailed"));
      await itemUpdateFn({ data: { itemId: item.id, patch: { image_path: s.path } } });
      toast.success(t("photo.uploaded")); announce(t("photo.uploadedAnnounce"), "polite"); onChange();
    } catch (e: any) {
      const msg = e.message || t("photo.uploadFailed");
      setErr("photo", msg);
      announce(t("photo.failedAnnounce", { msg: e.message || t("photo.unknownErr") }), "assertive");
    } finally { setUploading(false); }
  };

  const removePhoto = async () => {
    if (!confirm(t("photo.removeConfirm"))) return;
    await itemUpdateFn({ data: { itemId: item.id, patch: { image_path: null } } });
    toast.success(t("photo.removed")); announce(t("photo.removedAnnounce"), "polite"); onChange();
  };

  const L = ml(menu.default_language);
  const U = ul(menu.default_language);
  const uid = useId();
  const nameId = `${uid}-name`; const priceId = `${uid}-price`; const portionId = `${uid}-portion`;
  const availId = `${uid}-avail`; const descId = `${uid}-desc`; const fileId = `${uid}-file`; const fileId2 = `${uid}-file2`;

  return (
    <div className="rounded-lg border bg-card p-3 shadow-sm sm:p-4">

    <div className="flex flex-col gap-3 sm:grid sm:items-center sm:gap-3 sm:grid-cols-[112px_minmax(0,1fr)_110px_120px_140px_auto]">
        <div className="flex flex-col gap-1 sm:w-[112px]">
          <label
            htmlFor={fileId}
            className={`group relative flex aspect-video w-full shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded border bg-muted text-[10px] text-muted-foreground hover:bg-muted/70 focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${errors.photo ? "border-destructive ring-1 ring-destructive" : ""} ${uploading ? "opacity-80" : ""}`}
            title={`${L.photo} (${L.upload_hint})`}
            aria-label={`${thumbUrl ? U.replace : U.add}. ${L.upload_hint}`}
          >
            {thumbUrl ? (
              <>
                <FocusImg url={thumbUrl} alt={name || L.photo} focus={item.image_focus} className="h-full w-full" />
                {!uploading && (
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/45 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                    {U.replace}
                  </span>
                )}
              </>
            ) : null}
            {uploading && (
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-background/85 text-[11px] font-medium text-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span>{U.uploading}</span>
              </span>
            )}
            {!thumbUrl && !uploading && (
              <span className="px-1 text-center leading-tight">+ {U.add}</span>
            )}
            <input
              id={fileId}
              type="file"
              accept="image/jpeg,image/png"
              className="sr-only"
              aria-invalid={!!errors.photo}
              aria-describedby={`${fileId}-status${errors.photo ? ` ${fileId}-err` : ""}`}
              disabled={uploading}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.currentTarget.value = ""; }}
            />
          </label>
          {/* Textual live status for screen readers AND sighted users */}
          <p id={`${fileId}-status`} role="status" aria-live="polite" className="text-[11px] leading-tight text-muted-foreground">
            {uploading ? U.status_uploading : errors.photo ? "" : (thumbUrl ? U.status_ready : L.upload_hint)}
          </p>
          {errors.photo && (
            <p id={`${fileId}-err`} role="alert" className="text-[11px] leading-tight text-destructive">
              {U.status_error} {errors.photo}
            </p>
          )}
          {thumbUrl && !uploading && (
            <button
              type="button"
              onClick={removePhoto}
              className="self-start rounded text-[11px] text-destructive underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2"
              aria-label={`${L.remove_photo} — ${name || L.photo}`}
            >
              {L.remove_photo}
            </button>
          )}
        </div>



        <div className="grid min-w-0 gap-1">
          <Label htmlFor={nameId} className="text-xs text-muted-foreground sm:sr-only">{t("item.name")}</Label>
          <Input
            id={nameId}
            value={name}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? `${nameId}-err` : undefined}
            className={errors.name ? "border-destructive focus-visible:ring-destructive" : ""}
            onChange={(e) => { setName(e.target.value); if (errors.name && e.target.value.trim()) setErr("name", undefined); }}
            onBlur={async () => {
              if (!name.trim()) { setErr("name", t("item.nameReqBlur")); announce(t("item.nameReqBlur"), "assertive"); return; }
              setErr("name", undefined);
              if (name !== initialName) { await itemTrFn({ data: { itemId: item.id, language_code: menu.default_language, name: name.trim(), description: desc || null } }); announce(t("item.nameSavedAnnounce", { name: name.trim() }), "polite"); onChange(); }
            }} />
          {errors.name && <p id={`${nameId}-err`} role="alert" className="text-xs text-destructive">{errors.name}</p>}
        </div>

        <div className="grid grid-cols-2 gap-2 sm:contents">
          <div className="grid min-w-0 gap-1">
            <Label htmlFor={priceId} className="text-xs text-muted-foreground sm:sr-only">{t("item.price")}</Label>
            <Input id={priceId} inputMode="decimal" defaultValue={item.price ?? ""} placeholder={t("item.pricePh")}
              aria-invalid={!!errors.price}
              aria-describedby={errors.price ? `${priceId}-err` : undefined}
              className={errors.price ? "border-destructive focus-visible:ring-destructive" : ""}
              onBlur={async (e) => {
                const raw = e.target.value.trim();
                if (!raw) { setErr("price", undefined); await itemUpdateFn({ data: { itemId: item.id, patch: { price: null } } }); announce(t("item.priceClearedAnnounce"), "polite"); onChange(); return; }
                const v = Number(raw.replace(",", "."));
                if (!Number.isFinite(v) || v < 0) { setErr("price", t("item.priceInvalidBlur")); announce(t("item.priceInvalidAnnounce"), "assertive"); return; }
                setErr("price", undefined);
                await itemUpdateFn({ data: { itemId: item.id, patch: { price: v } } }); announce(t("item.priceSavedAnnounce", { v }), "polite"); onChange();
              }} />
            {errors.price && <p id={`${priceId}-err`} role="alert" className="text-xs text-destructive">{errors.price}</p>}
          </div>
          <div className="grid min-w-0 gap-1">
            <Label htmlFor={portionId} className="text-xs text-muted-foreground sm:sr-only">{t("item.portion")}</Label>
            <Input id={portionId} defaultValue={item.portion_size ?? ""} placeholder={t("item.portionPh")}
              onBlur={async (e) => { await itemUpdateFn({ data: { itemId: item.id, patch: { portion_size: e.target.value || null } } }); onChange(); }} />
          </div>
        </div>


        <div className="grid min-w-0 gap-1">
          <Label htmlFor={availId} className="text-xs text-muted-foreground sm:sr-only">{L.available}</Label>
          <Select defaultValue={item.availability_status} onValueChange={async (v) => { await itemUpdateFn({ data: { itemId: item.id, patch: { availability_status: v as any } } }); onChange(); }}>
            <SelectTrigger id={availId} className="w-full" aria-label={L.available}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available">{L.available}</SelectItem>
              <SelectItem value="sold_out">{L.sold_out}</SelectItem>
              <SelectItem value="days_only">{L.days_only}</SelectItem>
              <SelectItem value="time_only">{L.time_only}</SelectItem>
              <SelectItem value="hidden">{L.hidden}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2 sm:justify-start">
          <Button size="sm" variant="outline" className="flex-1 sm:flex-none" aria-expanded={expanded} onClick={() => setExpanded((e) => !e)}>{expanded ? L.collapse : L.expand}</Button>
          <Button size="sm" variant="ghost" aria-label={t("item.deleteAria", { delete: L.delete, name })} onClick={async () => { if (confirm(L.delete)) { await itemDelFn({ data: { itemId: item.id } }); onChange(); } }}><Trash2 className="h-4 w-4" aria-hidden="true" /></Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 grid gap-5 border-t pt-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor={descId}>{L.description} ({menu.default_language})</Label>
            <Textarea id={descId} rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} onBlur={async () => { await itemTrFn({ data: { itemId: item.id, language_code: menu.default_language, name: name.trim() || initialName, description: desc || null } }); onChange(); }} />
          </div>
          <div className="space-y-2">
            <Label htmlFor={fileId2}>{L.photo}</Label>
            <p id={`${fileId2}-hint`} className="text-xs text-muted-foreground">{L.upload_hint}</p>
            {thumbUrl ? (
              <div className="aspect-video w-full max-w-sm shrink-0 overflow-hidden rounded border bg-muted">
                <FocusImg url={thumbUrl} alt={name || L.photo} className="h-full w-full" />
              </div>
            ) : (
              <div className="aspect-video w-full max-w-sm shrink-0 overflow-hidden rounded border bg-muted">
                <div className="grid h-full w-full place-items-center text-[10px] text-muted-foreground">{L.no_photo}</div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <input id={fileId2} type="file" accept="image/jpeg,image/png" aria-describedby={`${fileId2}-hint`} className="text-xs file:mr-2 file:rounded file:border file:bg-background file:px-2 file:py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.currentTarget.value = ""; }} />
              {uploading && <span className="text-xs text-muted-foreground" aria-live="polite">…</span>}
              {item.image_path && (
                <Button size="sm" variant="outline" onClick={removePhoto}><Trash2 className="mr-1 h-3 w-3" aria-hidden="true" /> {L.remove_photo}</Button>
              )}
            </div>
          </div>

          <div className="space-y-2 border-t pt-4 md:col-span-2">
            <Label>{L.diet}</Label>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs">
              {(["is_vegetarian","is_vegan","is_gluten_free","is_lactose_free"] as const).map((k) => {
                const key = k.replace("is_", "") as "vegetarian" | "vegan" | "gluten_free" | "lactose_free";
                return (
                  <label key={k} className="flex items-center gap-1">
                    <input type="checkbox" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" defaultChecked={!!item[k]} onChange={async (e) => { await itemUpdateFn({ data: { itemId: item.id, patch: { [k]: e.target.checked } as any } }); onChange(); }} />
                    {L[key]}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="space-y-3 border-t pt-4 md:col-span-2">
            <div className="space-y-2">
              <Label>{L.contains}</Label>
              <div className="flex flex-wrap gap-2">
                {allergens.map((a: { id: number; code: string }) => (
                  <button key={a.id} type="button"
                    aria-pressed={cSet.includes(a.id)}
                    className={`rounded border px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${cSet.includes(a.id) ? "bg-primary text-primary-foreground" : "bg-background"}`}
                    onClick={() => setCSet((s) => toggle(s, a.id))}>
                    {a.id}. {allergenLabels[a.id] ?? a.code}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>{L.may_contain}</Label>
              <div className="flex flex-wrap gap-2">
                {allergens.map((a: { id: number; code: string }) => (
                  <button key={a.id} type="button"
                    aria-pressed={mSet.includes(a.id)}
                    className={`rounded border px-2 py-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${mSet.includes(a.id) ? "bg-secondary" : "bg-background"}`}
                    onClick={() => setMSet((s) => toggle(s, a.id))}>
                    {a.id}. {allergenLabels[a.id] ?? a.code}
                  </button>
                ))}
              </div>
            </div>
            <Button size="sm" onClick={async () => { await setAllFn({ data: { itemId: item.id, contains: cSet, may_contain: mSet } }); toast.success(L.save_allergens); onChange(); }}>{L.save_allergens}</Button>
          </div>
        </div>
      )}
    </div>
  );
}


function TranslationsTab({ data }: { data: any }) {
  const t = useMenuOwnerT();
  const qc = useQueryClient();
  const menu = data.menu;
  const trFn = useServerFn(upsertItemTranslation);
  const catTrFn = useServerFn(upsertCategoryTranslation);
  const menuTrFn = useServerFn(upsertMenuTranslation);
  const langs = (menu.available_languages ?? [menu.default_language]).filter((l: string) => l !== menu.default_language);
  const [activeLang, setActiveLang] = useState(langs[0] ?? "");
  if (langs.length === 0) return <p className="text-sm text-muted-foreground">{t("tr.addLangHint")}</p>;

  const invalidate = () => qc.invalidateQueries({ queryKey: ["menu-editor", menu.id] });

  const menuTr = data.translations.find((x: any) => x.language_code === activeLang) ?? {};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {langs.map((l: string) => (
          <Button key={l} size="sm" variant={l === activeLang ? "default" : "outline"} onClick={() => setActiveLang(l)}>{l.toUpperCase()}</Button>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-sm">{t("tr.menuOf", { lang: activeLang })}</CardTitle></CardHeader>
        <CardContent className="grid gap-3">
          <Input defaultValue={menuTr.title ?? ""} placeholder={t("tr.titlePh")} onBlur={(e) => menuTrFn({ data: { menuId: menu.id, language_code: activeLang, title: e.target.value, description: menuTr.description, footer_note: menuTr.footer_note } }).then(invalidate)} />
          <Textarea defaultValue={menuTr.description ?? ""} placeholder={t("tr.descPh")} onBlur={(e) => menuTrFn({ data: { menuId: menu.id, language_code: activeLang, title: menuTr.title, description: e.target.value, footer_note: menuTr.footer_note } }).then(invalidate)} />
        </CardContent>
      </Card>

      {data.categories.map((cat: any) => {
        const catTr = data.categoryTranslations.find((x: any) => x.category_id === cat.id && x.language_code === activeLang) ?? {};
        const catDef = data.categoryTranslations.find((x: any) => x.category_id === cat.id && x.language_code === menu.default_language);
        const items = data.items.filter((i: any) => i.category_id === cat.id);
        return (
          <Card key={cat.id}>
            <CardHeader><CardTitle className="text-sm">{t("tr.catLabel", { name: catDef?.name ?? "—" })}</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <Input defaultValue={catTr.name ?? ""} placeholder={t("tr.namePh")}
                onBlur={(e) => e.target.value.trim() && catTrFn({ data: { categoryId: cat.id, language_code: activeLang, name: e.target.value.trim() } }).then(invalidate)} />
              {items.map((it: any) => {
                const itDef = data.itemTranslations.find((x: any) => x.item_id === it.id && x.language_code === menu.default_language);
                const itTr = data.itemTranslations.find((x: any) => x.item_id === it.id && x.language_code === activeLang) ?? {};
                return (
                  <div key={it.id} className="grid gap-1 rounded border p-2">
                    <p className="text-xs text-muted-foreground">{t("tr.source", { name: itDef?.name ?? "—" })}</p>
                    <Input defaultValue={itTr.name ?? ""} placeholder={t("tr.namePh")}
                      onBlur={(e) => e.target.value.trim() && trFn({ data: { itemId: it.id, language_code: activeLang, name: e.target.value.trim(), description: itTr.description ?? null } }).then(invalidate)} />
                    <Textarea rows={2} defaultValue={itTr.description ?? ""} placeholder={t("tr.descPh")}
                      onBlur={(e) => trFn({ data: { itemId: it.id, language_code: activeLang, name: itTr.name ?? itDef?.name ?? "", description: e.target.value || null } }).then(invalidate)} />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}


function PreviewTab({ data, allergens }: { data: any; allergens: { id: number; code: string }[] }) {
  const t = useMenuOwnerT();
  const menu = data.menu;
  const [lang, setLang] = useState(menu.default_language);
  const langs: string[] = menu.available_languages?.length ? menu.available_languages : [menu.default_language];
  const catName = (id: string) => data.categoryTranslations.find((x: any) => x.category_id === id && x.language_code === lang)?.name
    ?? data.categoryTranslations.find((x: any) => x.category_id === id && x.language_code === menu.default_language)?.name ?? "";
  const itemT = (id: string) => data.itemTranslations.find((x: any) => x.item_id === id && x.language_code === lang)
    ?? data.itemTranslations.find((x: any) => x.item_id === id && x.language_code === menu.default_language) ?? {};
  const menuTr = data.translations.find((x: any) => x.language_code === lang) ?? data.translations.find((x: any) => x.language_code === menu.default_language) ?? {};

  return (
    <Card>
      <CardContent className="py-6">
        <div className="mb-4 flex flex-wrap gap-2">
          {langs.map((l: string) => (
            <Button key={l} size="sm" variant={l === lang ? "default" : "outline"} onClick={() => setLang(l)}>{l.toUpperCase()}</Button>
          ))}
        </div>
        <h2 className="mb-1 font-serif text-2xl">{menuTr.title || menu.public_name || menu.internal_name}</h2>
        {menuTr.description && <p className="mb-4 text-sm text-muted-foreground">{menuTr.description}</p>}

        {data.categories.filter((c: any) => c.is_active).map((cat: any) => {
          const items = data.items.filter((i: any) => i.category_id === cat.id && i.is_active && i.availability_status !== "hidden");
          if (items.length === 0) return null;
          return (
            <div key={cat.id} className="mb-6">
              <h3 className="mb-2 border-b pb-1 font-serif text-lg">{catName(cat.id)}</h3>
              <ul className="space-y-2">
                {items.map((it: any) => {
                  const tr = itemT(it.id);
                  const all = data.itemAllergens.filter((a: any) => a.item_id === it.id);
                  return (
                    <li key={it.id} className="flex flex-wrap items-start justify-between gap-3 border-b border-dashed pb-2">
                      {it.image_path && <PreviewThumb path={it.image_path} alt={tr.name ?? ""} focus={it.image_focus} />}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{tr.name} {it.portion_size && <span className="text-xs text-muted-foreground">({it.portion_size})</span>}</p>
                        {tr.description && <p className="text-xs text-muted-foreground">{tr.description}</p>}
                        {all.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {t("prev.allergens")}: {all.filter((a: any) => a.presence_type === "contains").map((a: any) => a.allergen_id).join(", ") || "—"}
                            {all.some((a: any) => a.presence_type === "may_contain") && <> · {t("prev.mayContain")}: {all.filter((a: any) => a.presence_type === "may_contain").map((a: any) => a.allergen_id).join(", ")}</>}
                          </p>
                        )}
                      </div>

                      <p className="font-medium tabular-nums">{it.price != null ? `${Number(it.price).toFixed(2)} ${menu.currency_code}` : ""}</p>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}

        {menuTr.footer_note && <p className="mt-6 text-xs text-muted-foreground">{menuTr.footer_note}</p>}
        <AllergenLegend allergens={allergens} lang={lang} />
      </CardContent>
    </Card>
  );
}

type Focus = { x: number; y: number; zoom: number };
function FocusImg({ url, alt, className }: { url: string; alt: string; focus?: Focus | null; className?: string }) {
  return (
    <img
      src={url}
      alt={alt}
      className={className}
      style={{ display: "block", objectFit: "contain", objectPosition: "center" }}
    />
  );
}

function PreviewThumb({ path, alt, focus }: { path: string; alt: string; focus?: Focus | null }) {
  const getUrlFn = useServerFn(getMenuImageSignedUrl);
  const { data: url } = useQuery({
    queryKey: ["menu-item-full-image", path],
    queryFn: () => getUrlFn({ data: { path } }),
    staleTime: 30 * 60 * 1000,
  });
  if (!url) return null;
  return (
    <div className="aspect-video w-32 shrink-0 overflow-hidden rounded">
      <FocusImg url={url} alt={alt} focus={focus} className="h-full w-full" />
    </div>
  );
}

function AllergenLegend({ allergens, lang }: { allergens: { id: number; code: string }[]; lang: string }) {
  const t = useMenuOwnerT();

  const { data: trs = [] } = useQuery({
    queryKey: ["allergen-tr", lang],
    queryFn: async () => {
      const { data } = await supabase.from("allergen_translations" as any).select("allergen_id, name").eq("language_code", lang);
      return ((data as unknown) ?? []) as { allergen_id: number; name: string }[];
    },
  });
  return (
    <div className="mt-6 rounded border bg-muted/40 p-3 text-xs">
      <p className="mb-1 font-medium">{t("prev.legend")}</p>
      <ul className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 md:grid-cols-3">
        {allergens.map((a: { id: number; code: string }) => {
          const tr = trs.find((x) => x.allergen_id === a.id);
          return <li key={a.id}>{a.id}. {tr?.name ?? a.code}</li>;
        })}
      </ul>
    </div>
  );
}
